import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { changePasswordAfterFirstLogin, getUserByEmail } from "../../features/search/service/user-service";
import { User } from "../../shared/types/user";
import InputComponent from "../../components/input/input";
import ButtonComponent from "../../components/button/button";
import './styles/reset-pwd.css';

export const ResetPwd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    let email: string | null = null;
    
    if (location.state && typeof location.state === 'object' && 'email' in location.state) {
        email = location.state.email;
    } 
    else if (location.state && typeof location.state === 'string') {
        email = location.state;
    }
    
    if (!email) {
        const searchParams = new URLSearchParams(location.search);
        email = searchParams.get('email');
    }

    const [user, setUser] = useState<User | null>(null);
    const [newPassword, setNewPassword] = useState<string>('');
    const [oldPassword, setOldPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchingUser, setFetchingUser] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');

    // Buscar usuário pelo email
    useEffect(() => {
        const fetchUser = async () => {
            if (!email) {
                setError('Email não fornecido. Faça login novamente.');
                setFetchingUser(false);
                return;
            }

            try {
                setFetchingUser(true);
                const userData = await getUserByEmail(email);
                
                if (!userData) {
                    console.error('❌ Usuário não encontrado');
                    setError('Usuário não encontrado.');
                    return;
                }

                setUser(userData);
                setError('');
            } catch (error) {
                console.error('❌ Erro ao buscar usuário:', error);
                setError('Erro ao carregar dados do usuário.');
            } finally {
                setFetchingUser(false);
            }
        };

        fetchUser();
    }, [email, location.state, location.search]);

    // Validar senha em tempo real
    useEffect(() => {
        if (newPassword && confirmPassword) {
            if (newPassword !== confirmPassword) {
                setPasswordError('As senhas não coincidem');
            } else if (newPassword.length < 6) {
                setPasswordError('A senha deve ter pelo menos 6 caracteres');
            } else {
                setPasswordError('');
            }
        } else {
            setPasswordError('');
        }
    }, [newPassword, confirmPassword]);

    const handleResetPassword = async () => {
        // Validações
        if (!newPassword || !confirmPassword) {
            setPasswordError('Preencha todos os campos');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('As senhas não coincidem');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('A senha deve ter pelo menos 6 caracteres');
            return;
        }

        if (!user?.id) {
            setError('Usuário não identificado');
            return;
        }

        setLoading(true);
        setError('');
        setPasswordError('');

        try {
            const response = await changePasswordAfterFirstLogin(location.state.email, newPassword, oldPassword);
            
            if (response) {
                setNewPassword('');
                setConfirmPassword('');
                navigate('/');
            }
        } catch (error) {
            console.error('❌ Erro ao redefinir senha:', error);
            setError('Erro ao redefinir senha. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Permitir envio ao pressionar Enter
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !loading && newPassword && confirmPassword && newPassword === confirmPassword && newPassword.length >= 6) {
            handleResetPassword();
        }
    };

    // Estado de carregamento
    if (fetchingUser) {
        return (
            <div className="reset-pwd-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-pwd-container">
            <img 
                src="../../../assets/logo-inteira.png" 
                alt="Logo delay discounting"  
                style={{ width: '200px', height: '130px', marginBottom: '20px' }}
            />

            <h1>Definir Nova Senha</h1>
            
            <p className="welcome-text">
                Olá, <strong>{user?.name}</strong>! 
            </p>
            <p className="subtitle">
                Este é seu primeiro acesso. Por favor, defina uma nova senha.
            </p>

            <div className="reset-pwd-form">
                <InputComponent 
                    id="id-input-old-password" 
                    label="Senha Antiga" 
                    type="password"
                    value={oldPassword} 
                    onChange={(event) => setOldPassword(event.target.value)}
                    onKeyPress={handleKeyPress}
                />

                <InputComponent 
                    id="id-input-new-password" 
                    label="Nova Senha" 
                    type="password"
                    value={newPassword} 
                    onChange={(event) => setNewPassword(event.target.value)}
                    onKeyPress={handleKeyPress}
                />

                <InputComponent 
                    id="id-input-confirm-password" 
                    label="Confirmar Nova Senha" 
                    type="password"
                    value={confirmPassword} 
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    onKeyPress={handleKeyPress}
                />

                <div className="password-requirements">
                    <p className="requirement-title">Requisitos da senha:</p>
                    <ul>
                        <li className={newPassword.length >= 6 ? 'valid' : ''}>
                            Pelo menos 6 caracteres
                        </li>
                        <li className={newPassword && confirmPassword && newPassword === confirmPassword ? 'valid' : ''}>
                            As senhas devem coincidir
                        </li>
                    </ul>
                </div>

                {passwordError && (
                    <div className="error-message">
                        <p>⚠️ {passwordError}</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <p>❌ {error}</p>
                    </div>
                )}

                <div className="button-container">
                    <ButtonComponent
                        label={loading ? "Salvando..." : "Definir Senha"}
                        onClick={handleResetPassword}
                        disabled={
                            loading || 
                            !newPassword || 
                            !confirmPassword || 
                            newPassword !== confirmPassword ||
                            newPassword.length < 6
                        }
                    />
                </div>
            </div>

            <div className="reset-pwd-footer">
                <p>
                    Após definir sua senha, você será redirecionado para o login.
                </p>
            </div>
        </div>
    );
};